import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IbanElement, useElements, useStripe } from '@stripe/react-stripe-js';
import translate from '../../translate';
import {
  attachPaymentMethod,
  getStripeClientSecret,
} from '../../../services/checkoutHelper';
import { compose, PAYMENT_METHOD } from '../../../services/utilityFunctions';
import withCSRF from '../../withCSRF';
import BillingProp from '../../../sections/profile/propTypes/billingProp';
import Notifications from '../../../services/notifications';
import StripeService from '../../../services/stripeService';

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ['SEPA'],
  style: {
    base: {
      iconColor: '#222222',
      color: '#222222',
      fontSize: '16px',
      fontWeight: 100,
      '::placeholder': {
        color: '#aab7c4',
      },
      ':-webkit-autofill': {
        color: '--primary',
      },
    },
    invalid: {
      color: '#f4516c',
      iconColor: '#f4516c',
      ':-webkit-autofill': {
        color: '#f4516c',
      },
    },
  },
};

function StripeSEPADebitForm({
  t,
  _csrf,
  id,
  billing,
  onFinish,
  onCancel,
  handleIsLoading,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);

  const companyName = billing?.billingAddress?.companyName ?? '';
  const [state, setState] = useState({
    accountName: companyName,
    accountEmail: billing?.invoiceEmail ?? '',
    accountIBAN: '',
  });

  const handleChange =
    field =>
    ({ target }) =>
      setState(prevState => ({ ...prevState, [field]: target.value }));

  const onSubmit = async event => {
    event.preventDefault();
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    handleIsLoading(true);
    const { accountName, accountEmail } = state;
    try {
      const clientSecret = await getStripeClientSecret(
        _csrf,
        PAYMENT_METHOD.SEPA_DEBIT,
      );
      const resultData = await stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
          sepa_debit: elements.getElement(IbanElement),
          billing_details: {
            name: accountName,
            email: accountEmail,
          },
        },
      });
      if (resultData.error) {
        console.log(resultData.error.message);
        const errCode = StripeService.handleErrors(resultData.error);
        Notifications.showNotificationError(t('error'), t(errCode));
        setError(errCode);
      } else {
        const { setupIntent } = resultData;
        const paymentMethod = await attachPaymentMethod(
          _csrf,
          setupIntent.payment_method,
        );
        onFinish(paymentMethod);
      }
    } catch (err) {
      setError(err);
    }
    handleIsLoading(false);
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="stripe-container">
        <div className="payment-line">
          <div className="payment-column">
            <div className="payment-input">
              <label htmlFor="accountName">
                {t('sepa_account_holder_name')}
                <input
                  name="accountName"
                  required
                  value={state.accountName}
                  onChange={handleChange('accountName')}
                />
              </label>
            </div>
          </div>
          <div className="payment-column">
            <div className="payment-input">
              <label htmlFor="accountEmail">
                {t('sepa_account_holder_email')}
                <input
                  name="accountEmail"
                  type="accountEmail"
                  required
                  value={state.accountEmail}
                  onChange={handleChange('accountEmail')}
                />
              </label>
            </div>
          </div>
        </div>
        <label htmlFor={id}>{t('IBAN')}</label>
        <div className="stripe-input-container">
          <div id={id} />
          <IbanElement
            name="accountIBAN"
            options={{
              ...IBAN_ELEMENT_OPTIONS,
              placeholderCountry: billing?.billingAddress?.countryCode ?? 'LU',
            }}
          />
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="stripe-button stripe-cancel-button"
            >
              {t('cancel')}
            </button>
          )}
          <button
            type="submit"
            // onClick={handleAdd}
            className="stripe-button stripe-finish-button"
          >
            {t('add_sepa')}
          </button>
        </div>
        <div className="stripe-error-container" role="alert">
          {error ? t(error) : ''}
        </div>
        <div className="mandate-acceptance">{t('sepa_mandate_acceptance')}</div>
      </div>
    </form>
  );
}

StripeSEPADebitForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  id: PropTypes.string,
  onFinish: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  handleIsLoading: PropTypes.func.isRequired,
  billing: PropTypes.shape(BillingProp.propType),
};

StripeSEPADebitForm.defaultProps = {
  id: 'stripe-iban-input',
  billing: BillingProp.defaultProp,
};

export default compose(translate, withCSRF)(StripeSEPADebitForm);
